/**
 * PDP ad/CRM discount DISPLAY helpers.
 *
 * Pure functions shared by the PDP discount snippets (giftory-ad-discount.liquid and
 * product-page-data.liquid), extracted here so the pricing math can be unit-tested in
 * isolation — mirrors assets/dd-cart.js for the cart side. No DOM, no Alpine `this`.
 *
 * The PDP prices a coupon speculatively via Storefront `cartCreate` phantom carts. Since
 * GFTY-5303 each variant is verified in its OWN qty-1 single-line cart, whose line total
 * is the authoritative discounted price (min-spend and entitled-product rules included);
 * `discountAllocations[].sourceDiscountApplication.value` (PricingValue = MoneyV2 |
 * PricingPercentageValue) tells a fixed $ code from a percentage one.
 *
 * Why type matters: the AB "double-dipping" treatment re-bases the coupon onto
 * price_on_giftory (POG). A percentage code keeps its fraction on the higher base; a
 * fixed dollar code must keep its exact dollar amount — modelling it as a fraction of the
 * variant price gave the wrong price/% for $-off crmcodes (e.g. OFFERAMC $37.5 off).
 *
 * Units: all *Cents args are CENTS. POG is passed in cents by callers.
 */
(function () {
  'use strict';

  // Discount type tokens. Values intentionally match Shopify's REST
  // discount_application.value_type (what the cart side, dd-coupon-bridge.liquid, reads).
  var TYPE = { FIXED: 'fixed_amount', PERCENTAGE: 'percentage' };

  /**
   * Read a cart line's discount TYPE + configured value from the Storefront
   * `discountAllocations` array. When `code` is given, only that code's allocation is
   * considered — a line can also carry automatic/storewide-sale allocations, which would
   * otherwise misclassify the type or feed the wrong amount. Uses `sourceDiscountApplication`
   * (the code's DEFINITION value), not the deprecated per-line-allocated `discountApplication`.
   * @param {Array} discountAllocations - line.discountAllocations from cartCreate
   * @param {string} [code] - the applied discount code to scope to (CartCodeDiscountAllocation.code)
   * @returns {{type:'fixed_amount', discountValueCents:number} | {type:'percentage', percentage:number} | null}
   */
  function parseDiscountValue(discountAllocations, code) {
    var allocs = discountAllocations || [];
    for (var i = 0; i < allocs.length; i++) {
      var alloc = allocs[i];
      if (!alloc) continue;
      // Skip automatic / other-code allocations. Case-insensitive: Shopify codes match case-insensitively,
      // and the URL-param casing (winner.code) can differ from the stored CartCodeDiscountAllocation.code.
      if (code != null && String(alloc.code).toUpperCase() !== String(code).toUpperCase()) continue;
      var app = alloc.sourceDiscountApplication;
      var value = app && app.value;
      if (!value) continue;
      if (value.__typename === 'MoneyV2') {
        var cents = Math.round(parseFloat(value.amount) * 100);
        if (!isNaN(cents)) return { type: TYPE.FIXED, discountValueCents: cents };
      } else if (value.__typename === 'PricingPercentageValue') {
        var pct = parseFloat(value.percentage);
        if (!isNaN(pct)) return { type: TYPE.PERCENTAGE, percentage: pct };
      }
    }
    return null;
  }

  /**
   * Build a published-map entry from a qty-1 SINGLE-LINE cartCreate result (GFTY-5303).
   * The aggregate phantom cart lets Shopify satisfy a code's minimum-spend from the summed
   * subtotal, so per-variant eligibility must come from a cart holding ONLY that variant:
   * `discountCodes[].applicable` there is the checkout truth, and the line's totalAmount is
   * the exact qty-1 price (no ACROSS/EACH recompute). Returns null when the code does not
   * apply to this variant alone, so the caller omits the variant from the map.
   * bestCode carries the cart's STORED casing (what checkout shows), not the URL-param casing.
   * @param {Object|null} cart - cartCreate.cart for a single-line cart
   * @param {string} code - the winner code (any casing)
   * @returns {{totalAfterDiscountCents:number, bestCode:string, discountType?:string} | null}
   */
  function verifiedEntryFromCart(cart, code) {
    if (!cart) return null;
    var codes = cart.discountCodes || [];
    var matched = null;
    for (var i = 0; i < codes.length; i++) {
      var c = codes[i];
      if (c && c.applicable && String(c.code).toUpperCase() === String(code).toUpperCase()) { matched = String(c.code); break; }
    }
    if (matched == null) return null;
    var edge = cart.lines && cart.lines.edges && cart.lines.edges[0];
    if (!edge) return null;
    var cents = Math.round(parseFloat(edge.node.cost.totalAmount.amount) * 100);
    if (isNaN(cents)) return null;
    var entry = { totalAfterDiscountCents: cents, bestCode: matched };
    var dv = parseDiscountValue(edge.node.discountAllocations, code);
    if (dv) entry.discountType = dv.type;
    return entry;
  }

  /**
   * Re-base a coupon's discount onto price_on_giftory for the double-dipping treatment.
   *  - fixed_amount: the SAME dollar amount comes off POG (off = variantPrice - total),
   *    so a $37.5-off code shows $37.5 off POG. Floored at 0.
   *  - percentage / unknown: the coupon FRACTION (total / variantPrice) is applied to POG,
   *    so an X%-off code shows X% off POG (unchanged legacy behaviour).
   * Returns totalAfterDiscountCents unchanged when POG/price are unusable.
   * @param {number} totalAfterDiscountCents - the coupon-priced total against the variant price
   * @param {number} variantPriceCents
   * @param {number} pogCents - price_on_giftory in cents
   * @param {string} [discountType] - 'fixed_amount' | 'percentage'
   * @returns {number}
   */
  function doubleDipCents(totalAfterDiscountCents, variantPriceCents, pogCents, discountType) {
    if (isNaN(pogCents) || isNaN(variantPriceCents) || variantPriceCents <= 0) return totalAfterDiscountCents;
    if (discountType === TYPE.FIXED) {
      var off = variantPriceCents - totalAfterDiscountCents; // exact $ the code removes at this variant
      return Math.max(0, Math.round(pogCents - off));
    }
    return Math.round(pogCents * (totalAfterDiscountCents / variantPriceCents));
  }

  /**
   * Per-variant line totals (CENTS) from a cartCreate result, keyed by NUMERIC variant id
   * (GFTY-5375). Run with NO discount codes, this is what Shopify charges today — flash sale and
   * any other automatic discount already included, because phantom carts surface automatic
   * allocations (which is why parseDiscountValue has to scope by code).
   *
   * That makes it the honest baseline a coupon has to beat: we never model the flash ourselves,
   * so it holds whether a non-combinable code DROPS the flash or stacks on top of it.
   *
   * @param {Object|null} cart - cartCreate.cart
   * @returns {Object} { '<variantId>': cents }
   */
  function lineTotalsByVariantId(cart) {
    var out = {};
    var edges = (cart && cart.lines && cart.lines.edges) || [];
    for (var i = 0; i < edges.length; i++) {
      var node = edges[i] && edges[i].node;
      var gid = node && node.merchandise && node.merchandise.id;
      if (!gid) continue;
      var id = String(gid).split('/').pop();
      var cents = Math.round(parseFloat(node.cost.totalAmount.amount) * 100);
      if (id && !isNaN(cents)) out[id] = cents;
    }
    return out;
  }

  /**
   * Whether a coupon-priced total should be kept for a variant (GFTY-5375).
   *
   * The comparison only runs when the line already carries an automatic discount, i.e. the no-code
   * baseline is BELOW the variant price — in practice a live flash sale. There the coupon must be
   * STRICTLY cheaper, because the two are non-combinable and a losing coupon would strip a better
   * flash price.
   *
   * With no automatic discount (baseline == variant price) the coupon is kept unconditionally,
   * preserving pre-GFTY-5375 behaviour on ordinary products — including a code that happens to
   * save nothing. Scope is the flash-vs-code conflict, not zero-value codes in general.
   *
   * Fails OPEN when the baseline is unknown (request failed, variant missing from the cart): the
   * baseline is a single request while the coupon probes are N against the same endpoint, so a
   * missing baseline almost always means there is no entry to judge anyway. Failing closed here
   * would let one blip disable every discount on the site. An unknown VARIANT price fails closed
   * instead — we can't rule out a flash, so the comparison stands.
   *
   * @param {number} totalAfterDiscountCents
   * @param {number|undefined} baselineCents - from lineTotalsByVariantId()
   * @param {number|undefined} variantPriceCents - the undiscounted variant price
   * @returns {boolean}
   */
  function beatsBaseline(totalAfterDiscountCents, baselineCents, variantPriceCents) {
    if (typeof baselineCents !== 'number' || isNaN(baselineCents)) return true;
    var price = parseFloat(variantPriceCents);
    if (!isNaN(price) && baselineCents >= price) return true; // no automatic discount on this line
    return totalAfterDiscountCents < baselineCents;
  }

  /**
   * The no-code baseline worth recording on a kept entry (GFTY-5375), or undefined.
   *
   * Only a baseline BELOW the variant price is recorded: that's a line with an automatic discount
   * (in practice a live flash sale), whose on-screen price is the flash price rather than the
   * variant price. teaserBaseCents() and needsQtyRecheck() both key off its presence. An unknown
   * variant price records it, mirroring beatsBaseline(): a flash can't be ruled out.
   *
   * @param {number|undefined} baselineCents - from lineTotalsByVariantId()
   * @param {number|undefined} variantPriceCents
   * @returns {number|undefined}
   */
  function flashBaselineCents(baselineCents, variantPriceCents) {
    if (typeof baselineCents !== 'number' || isNaN(baselineCents)) return undefined;
    var price = parseFloat(variantPriceCents);
    if (!isNaN(price) && baselineCents >= price) return undefined; // no automatic discount on this line
    return baselineCents;
  }

  /**
   * What the pre-apply "Extra X% OFF" teaser is measured against, in CENTS (GFTY-5375).
   *
   * The teaser is marginal: the extra the code takes off what is ON SCREEN. On a flash line the
   * screen shows the flash price until Apply, so measuring against the full variant price
   * overstated it ($100, flash $80, code $75 showed 25% instead of 6%). Falls back to the variant
   * price when the entry carries no flash baseline, so ordinary products are unchanged.
   *
   * @param {Object|null} entry - a published-map entry (may carry baselineCents)
   * @param {number} variantPriceCents
   * @returns {number}
   */
  function teaserBaseCents(entry, variantPriceCents) {
    var flash = entry ? flashBaselineCents(entry.baselineCents, variantPriceCents) : undefined;
    return flash !== undefined ? flash : parseFloat(variantPriceCents);
  }

  /**
   * Whether the qty-1 "code beats flash" verdict must be re-checked at the quantity actually being
   * added (GFTY-5375). The comparison runs at qty 1, but a once-per-order fixed code doesn't scale
   * with quantity while a per-unit flash does: $100, flash $15 off each, code $20 off once → the
   * code wins at qty 1 ($80 vs $85) and loses at qty 3 ($280 vs $255).
   *
   * Only entries with a flash baseline need it. Without an automatic discount there's nothing for
   * the code to strip, so the add flow keeps its pre-GFTY-5375 behaviour and makes no request.
   *
   * @param {Object|null} entry - the variant's published-map entry
   * @param {number} qty
   * @returns {boolean}
   */
  function needsQtyRecheck(entry, qty) {
    return !!entry && Number(qty) > 1 && typeof entry.baselineCents === 'number';
  }

  /**
   * Whether the code still wins at `qty`, from two qty-N single-line phantom carts (GFTY-5375).
   * Same rule as beatsBaseline(), against the qty-N line price.
   *
   * @param {number} codeTotalCents - qty-N line total WITH the code; NaN when it isn't applicable
   * @param {number|undefined} baselineCents - qty-N line total with NO code (unknown → fails open)
   * @param {number} unitPriceCents - undiscounted variant price
   * @param {number} qty
   * @returns {boolean}
   */
  function codeWinsAtQty(codeTotalCents, baselineCents, unitPriceCents, qty) {
    // Not applicable at this quantity: redirecting through it would change nothing at best.
    if (typeof codeTotalCents !== 'number' || isNaN(codeTotalCents)) return false;
    return beatsBaseline(codeTotalCents, baselineCents, parseFloat(unitPriceCents) * Number(qty));
  }

  var api = {
    TYPE: TYPE,
    parseDiscountValue: parseDiscountValue,
    doubleDipCents: doubleDipCents,
    verifiedEntryFromCart: verifiedEntryFromCart,
    lineTotalsByVariantId: lineTotalsByVariantId,
    beatsBaseline: beatsBaseline,
    flashBaselineCents: flashBaselineCents,
    teaserBaseCents: teaserBaseCents,
    needsQtyRecheck: needsQtyRecheck,
    codeWinsAtQty: codeWinsAtQty
  };
  if (typeof window !== 'undefined') window.PDPDiscount = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
